import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Phone, Mail, Globe, MapPin, User, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ViewCompanyContact() {
  const { leadId, companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCompanyContact();
  }, [leadId, companyId]);

  const fetchCompanyContact = async () => {
    try {
      const response = await axios.get(`${API}/lead-view-company/${leadId}/${companyId}`);
      setCompany(response.data.company);
      setLead(response.data.lead);
      setLoading(false);
      
      // Show success message
      toast.success('Company has been notified!', {
        description: 'They know you viewed their contact info and will prioritize reaching out.'
      });
    } catch (err) {
      console.error('Error fetching company contact:', err);
      setError('Unable to load company contact information. Please try again.');
      setLoading(false);
    }
  };

  const handleWebsiteClick = async (websiteUrl) => {
    try {
      // Track website click and trigger hot lead alert
      await axios.post(`${API}/track-website-click/${leadId}/${companyId}`);
      
      // Open website in new tab
      window.open(websiteUrl, '_blank');
      
      toast.success('Company alerted!', {
        description: 'They know you\'re visiting their website!'
      });
    } catch (err) {
      console.error('Error tracking website click:', err);
      // Still open the website even if tracking fails
      window.open(websiteUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading contact information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-blue-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-red-100 hover:text-white mb-4 transition"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              {company?.company_name}
            </h1>
            <p className="text-red-100">
              Property Management Company
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Alert */}
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-green-600 text-2xl">✅</div>
            <div>
              <h3 className="font-bold text-green-900 mb-1">Company Notified!</h3>
              <p className="text-green-800 text-sm">
                {company?.company_name} has been alerted that you viewed their contact information. 
                They will prioritize reaching out to you!
              </p>
            </div>
          </div>
        </div>

        {/* Company Details Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="h-8 w-8" />
              <h2 className="text-2xl font-bold">{company?.company_name}</h2>
            </div>
            {company?.description && (
              <p className="text-red-100 text-sm mt-2">
                {company.description}
              </p>
            )}
          </div>

          <div className="p-6 space-y-6">
            {/* Contact Person */}
            {company?.contact_person && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <User className="h-6 w-6 text-slate-600 mt-1" />
                <div>
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Contact Person</div>
                  <div className="text-lg font-semibold text-slate-900">{company.contact_person}</div>
                </div>
              </div>
            )}

            {/* Phone */}
            {company?.phone && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <Phone className="h-6 w-6 text-green-600 mt-1" />
                <div className="flex-1">
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Phone Number</div>
                  <a 
                    href={`tel:${company.phone}`}
                    className="text-lg font-semibold text-green-600 hover:text-green-800 hover:underline"
                  >
                    {company.phone}
                  </a>
                  <div className="mt-2">
                    <a
                      href={`tel:${company.phone}`}
                      className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition"
                    >
                      📞 Call Now
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Email */}
            {company?.email && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <Mail className="h-6 w-6 text-blue-600 mt-1" />
                <div className="flex-1">
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Email Address</div>
                  <a 
                    href={`mailto:${company.email}`}
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline break-all"
                  >
                    {company.email}
                  </a>
                  <div className="mt-2">
                    <a
                      href={`mailto:${company.email}`}
                      className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
                    >
                      📧 Send Email
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Website */}
            {company?.website && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <Globe className="h-6 w-6 text-purple-600 mt-1" />
                <div className="flex-1">
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Website</div>
                  <button
                    onClick={() => handleWebsiteClick(company.website)}
                    className="text-lg font-semibold text-purple-600 hover:text-purple-800 hover:underline break-all text-left"
                  >
                    {company.website}
                  </button>
                  <div className="mt-2">
                    <button
                      onClick={() => handleWebsiteClick(company.website)}
                      className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition"
                    >
                      🌐 Visit Website
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {company?.address && (
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <MapPin className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <div className="text-sm text-slate-500 font-semibold uppercase mb-1">Office Address</div>
                  <div className="text-lg text-slate-900">{company.address}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-3">💡 Next Steps</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>Reach out to {company?.company_name} using the contact information above</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Ask about their services, fees, and experience with {lead?.property_type || 'your property type'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>Request references from current property owners they manage</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>Compare with other matched companies before making your decision</span>
            </li>
          </ul>
        </div>

        {/* Support */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Questions or need help?</p>
          <a 
            href="mailto:info@topprop.ca" 
            className="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
          >
            info@topprop.ca
          </a>
        </div>
      </div>
    </div>
  );
}
